<template>
    <div class="widget-config-select-extended">
        <div class="mb-2">
            <RedesignFormLabel
                :label="templateParameterHelpName"
                :info-text="templateParameterHelpDescription"
                :required="required"
            />
            <div
                v-if="widgetTitles"
                class="text-main-xs text-gray-500 mt-0.5"
            >
                <span class="font-semibold">{{ t('widget.filter.appliesTo') }}:</span>
                {{ widgetTitles }}
            </div>
        </div>
        <div :class="horizontal ? 'flex items-end gap-3' : 'space-y-3'">
            <div :class="{ 'flex-1 min-w-0': horizontal }">
                <RedesignFormLabel
                    :label="t('widget.selectCategory')"
                    class="mb-1"
                />
                <VcSelect
                    :model-value="selectedCategoryId"
                    :multiple="false"
                    :loading="loading"
                    :options="categories"
                    :config="{
                        labelKey: 'categoryName',
                        valueKey: 'categoryID'
                    }"
                    :filterable="true"
                    :class="{ 'border-destructive-actions rounded': errorMessage }"
                    @update:modelValue="onCategoryChange"
                />
            </div>

            <div :class="{ 'flex-1 min-w-0': horizontal }">
                <RedesignFormLabel
                    :label="t('widget.selectQuestion')"
                    class="mb-1"
                />
                <VcSelect
                    :model-value="selectedQuestionLabel"
                    :multiple="false"
                    :loading="loading"
                    :options="currentQuestions"
                    :config="{
                        labelKey: 'label',
                        valueKey: 'label'
                    }"
                    :filterable="true"
                    :class="{ 'border-destructive-actions rounded': errorMessage }"
                    @update:modelValue="onQuestionChange"
                >
                    <template #option="{ option }">
                        <VcPopover
                            trigger="hover"
                            :placement="isRTL ? 'right' : 'left'"
                            :disabled="!option.description"
                        >
                            <template #reference>
                                <span class="block truncate">{{ option.label }}</span>
                            </template>
                            <div class="custom-tooltip-content p-4">
                                <div>
                                    <span class="font-medium">{{ t('general.question') }}:</span>
                                    {{ option.description }}
                                </div>
                                <div
                                    v-if="option.data_type"
                                    class="mt-2"
                                >
                                    <span class="font-medium">{{ t('general.dataType') }}:</span>
                                    {{ option.data_type }}
                                </div>
                            </div>
                        </VcPopover>
                    </template>
                </VcSelect>
            </div>
        </div>

        <RedesignFormError
            v-if="errorMessage"
            :error-message="errorMessage"
            class="mt-1"
        />
    </div>
</template>

<script setup lang="ts">
import RedesignFormLabel from '@/components/RedesignBaseComponents/Form/RedesignFormLabel.vue'
import RedesignFormError from '@/components/RedesignBaseComponents/Form/RedesignFormError.vue'
import { WidgetConfig } from '@/types/dashboard'
import { computed, onMounted, ref, watch } from 'vue'
import {
    getTemplateParameterHelpDescription,
    getTemplateParameterHelpName,
    templateDataTypesKey
} from '@/components/RedesignComponents/Widgets/widget.helper'
import { widgetParameterApi, type QuestionCategoriesMap, type QuestionCategory } from '@/api/widgetParameterApi'
import { isRTL } from '@/composables/useDirection'

import { useI18n } from 'vue-i18n'
import { VcSelect, VcPopover } from '@voicenter-team/voicenter-ui-plus'

const { t } = useI18n()

const props = withDefaults(defineProps<{
    value: WidgetConfig
    required?: boolean
    errorMessage?: string
    horizontal?: boolean
}>(), {
    required: false,
    errorMessage: '',
    horizontal: false
})

const emit = defineEmits<{
    (e: 'input', payload: WidgetConfig): void
    (e: 'update:value', payload: WidgetConfig): void
}>()

const categoriesMap = ref<QuestionCategoriesMap>({})
const selectedCategoryId = ref<number>()
const loading = ref(false)

const model = computed({
    get () {
        return props.value
    },
    set (val) {
        emit('input', val)
        emit('update:value', val)
    }
})

const templateParameterHelpName = computed(() => getTemplateParameterHelpName(props.value))
const templateParameterHelpDescription = computed(() => getTemplateParameterHelpDescription(props.value))
const selectedQuestionLabel = computed(() => model.value.WidgetParameterValue ?? '')

const allowedDataTypes = computed(() => props.value.allowedDataTypes ?? [])

const widgetTitles = computed(() => (props.value.widgetTitles ?? []).join(', '))

const categories = computed<QuestionCategory[]>(() => Object.values(categoriesMap.value))

const currentQuestions = computed(() => {
    if (!selectedCategoryId.value) return []
    return categoriesMap.value[String(selectedCategoryId.value)]?.questions || []
})

function findCategoryByQuestion (questionLabel: string): QuestionCategory | undefined {
    return categories.value.find(cat =>
        cat.questions.some(q => q.label === questionLabel)
    )
}

function initializeSelection () {
    const allCategories = categories.value
    if (!allCategories.length) return

    const currentValue = model.value.WidgetParameterValue

    if (currentValue) {
        const matchingCategory = findCategoryByQuestion(String(currentValue))
        if (matchingCategory) {
            selectedCategoryId.value = matchingCategory.categoryID
            return
        }
    }

    const firstCategory = allCategories[0]
    selectedCategoryId.value = firstCategory.categoryID

    if (firstCategory.questions.length) {
        updateValue(firstCategory.questions[0].label)
    }
}

function updateValue (questionLabel: string) {
    model.value.WidgetParameterValue = questionLabel
    emit('update:value', model.value)
}

function onCategoryChange (categoryId: number) {
    selectedCategoryId.value = categoryId
    const category = categoriesMap.value[String(categoryId)]
    if (category?.questions.length) {
        updateValue(category.questions[0].label)
    }
}

function onQuestionChange (questionLabel: string) {
    if (questionLabel === selectedQuestionLabel.value) return
    updateValue(questionLabel)
}

async function getData () {
    loading.value = true
    try {
        categoriesMap.value = await widgetParameterApi.getQuestionsList(allowedDataTypes.value)
        initializeSelection()
    } finally {
        loading.value = false
    }
}

onMounted(getData)

/*
 * Controls are keyed by position in the settings list, so adding or removing a widget can hand this
 * instance a parameter belonging to a different set of data types without remounting it. Watching the
 * key rather than the array avoids refetching every time an unrelated edit replaces the config object.
 */
watch(
    () => templateDataTypesKey(allowedDataTypes.value),
    () => {
        getData()
    }
)
</script>

<style lang="scss">
.custom-tooltip-content {
  max-height: 400px;
  overflow-y: scroll;
}
</style>
